import React, { useState, useEffect, useRef } from 'react';
import { fetchDailyAnalytics, fetchWeekendAnalytics } from '../api/client';
import { relevantWeekend, currentIsraeliWeek, israelToday, shiftStr, fmtShortDate, fmtShortRange } from '../components/DateRangePicker';
import RankingTable from '../components/RankingTable';
import Poster from '../components/Poster';
import Podium from '../components/Podium';
import WednesdayChip from '../components/WednesdayChip';
import { MoreButton } from '../components/VenueCharts';
import {
  PAGE_MAX, fmt, card, ltr, Chip, ChipRow, Head, Stepper, PhoneRow, CustomRange, Change, NewBadge, Occ,
  PresaleTag, fairChange,
} from '../components/RankingParts';
import useWindowWidth from '../hooks/useWindowWidth';

/**
 * טבלאות (§12.13) — one ranking of films over a day, a weekend, a week or a
 * custom range: the period as chips, ‹ › beside the title (into the future
 * too — a coming weekend is its presales), the card's own total on the left,
 * a podium of five (three on a phone) and the table from #1. The change
 * column appears only where the comparison is fair (`fairChange`).
 * The selection lives in App, so a visit to a film and back lands here, and
 * "מה חדש" and "סופי שבוע" open a given day or weekend through it.
 */

const MODES = [
  { key: 'today', label: 'יום' },
  { key: 'weekend', label: 'סופ״ש' },
  { key: 'week', label: 'שבוע' },
  { key: 'custom', label: 'טווח מותאם' },
];
const TITLE = { today: 'יום', weekend: 'סופ״ש', week: 'שבוע', custom: 'טווח מותאם' };
const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

export const defaultChartsView = () => ({ mode: 'weekend', ...relevantWeekend() });

const yearOf = (s) => Number(s.slice(0, 4));
function periodLabel(sel, today) {
  const other = yearOf(sel.start) !== yearOf(today) ? `.${yearOf(sel.start)}` : '';
  if (sel.mode === 'today') {
    if (sel.start === today) return <>היום · {ltr(fmtShortDate(today))}</>;
    const [y, m, d] = sel.start.split('-').map(Number);
    return <>{WEEKDAYS[new Date(y, m - 1, d).getDay()]} · {ltr(`${fmtShortDate(sel.start)}${other}`)}</>;
  }
  return ltr(`${fmtShortRange(sel.start, sel.end)}${other}`);
}

// The two endpoint shapes as one: rows by film, and the card's total.
function normalize(mode, data) {
  if (!data) return null;
  const day = mode === 'today';
  const rows = (data.by_movie || []).map((m) => ({
    movie_id: m.movie_id, title: m.title, title_he: m.title_he, title_en: m.title_en,
    poster_url: m.poster_url, genre: m.genre,
    tickets: (day ? m.day_tickets : m.weekend_tickets) || 0,
    screenings: (day ? m.day_screenings : m.weekend_screenings) || 0,
    prev: (day ? m.prev_tickets : m.prev_weekend_tickets) || 0,
    occupancy: m.occupancy || 0, total: m.total_tickets || 0,
    lastRank: m.last_week_rank, weeks: m.weeks_in_release,
    wednesday: !!m.wednesday_preview, previewTickets: m.preview_tickets || 0,
  })).filter((r) => r.tickets > 0).sort((a, b) => b.tickets - a.tickets);
  rows.forEach((r, i) => { r.place = i + 1; });
  return {
    rows,
    total: data.total_tickets || 0,
    prev: (day ? data.prev_tickets : data.prev_weekend_tickets) || 0,
    screenings: data.total_screenings || 0,
    occupancy: Math.round(data.avg_occupancy || 0),
  };
}

const filmName = (r) => r.title_he || r.title;
const filmPoster = (r, size) => <Poster posterUrl={r.poster_url} genre={r.genre} letter={(filmName(r) || '?')[0]} size={size} />;

// ▲2 / ▼1 against last week's place; "חדש" for a film that had none.
function Movement({ r }) {
  if (r.lastRank == null) return <NewBadge />;
  const d = r.lastRank - r.place;
  if (d === 0) return <span style={{ color: 'var(--faint)' }}>—</span>;
  return <span dir="ltr" style={{ unicodeBidi: 'isolate', fontWeight: 700, fontSize: 12, color: d > 0 ? 'var(--ok)' : 'var(--danger)' }}>{d > 0 ? '▲' : '▼'}{Math.abs(d)}</span>;
}

function FilmCell({ r }) {
  const en = r.title_en && r.title_en !== filmName(r) ? r.title_en : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {filmPoster(r, 'sm')}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--txt)' }}>{filmName(r)}</div>
        {(r.wednesday || en) && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 1 }}>
            {r.wednesday && <WednesdayChip tickets={r.previewTickets} />}
            {en && <span style={{ fontSize: 11, color: 'var(--faint)' }}>{en}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ChartsPage({ onMovieClick, refreshNonce, view, onViewChange }) {
  const [own, setOwn] = useState(defaultChartsView);
  const sel = view || own;
  const setSel = onViewChange || setOwn;
  const today = israelToday();
  const mob = useWindowWidth() < 768;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shown, setShown] = useState(20);
  const seq = useRef(0);

  useEffect(() => {
    const my = ++seq.current;
    setLoading(true);
    const req = sel.mode === 'today' ? fetchDailyAnalytics(sel.start) : fetchWeekendAnalytics(sel.start, sel.end);
    req.then((d) => { if (my === seq.current) { setData(normalize(sel.mode, d)); setLoading(false); } })
      .catch(() => { if (my === seq.current) { setData(null); setLoading(false); } });
  }, [sel.mode, sel.start, sel.end, refreshNonce]);
  useEffect(() => { setShown(20); }, [sel.mode, sel.start, sel.end]);

  const pick = (mode) => {
    if (mode === 'today') setSel({ mode, start: today, end: today });
    else if (mode === 'weekend') setSel({ mode, ...relevantWeekend() });
    else if (mode === 'week') setSel({ mode, ...currentIsraeliWeek() });
    else setSel({ ...sel, mode: 'custom' });
  };
  const step = (dir) => {
    const days = sel.mode === 'today' ? 1 : 7;
    setSel({ ...sel, start: shiftStr(sel.start, dir * days), end: shiftStr(sel.end, dir * days) });
  };

  const kind = sel.mode === 'today' ? 'day' : sel.mode;
  // A change, and occupancy in colour, only for a period that is not still selling.
  const withChange = fairChange(kind, sel, today);
  const rows = data?.rows || [];
  const steppable = sel.mode !== 'custom';

  const cols = [
    { key: 'lastRank', header: 'תנועה', align: 'center', sortValue: (r) => (r.lastRank == null ? -Infinity : r.lastRank - r.place),
      render: (r) => <Movement r={r} /> },
    { key: 'title', header: 'סרט', align: 'right', sortValue: filmName, render: (r) => <FilmCell r={r} /> },
    { key: 'tickets', header: 'כרטיסים', align: 'center', render: (r) => <b style={{ color: 'var(--accent)' }}>{fmt(r.tickets)}</b> },
    ...(withChange ? [{ key: 'change', header: 'שינוי', align: 'center',
                        sortValue: (r) => (r.prev ? (r.tickets - r.prev) / r.prev : Infinity),
                        render: (r) => <Change curr={r.tickets} prev={r.prev} /> }] : []),
    { key: 'screenings', header: 'הקרנות', align: 'center', render: (r) => <span style={{ color: 'var(--muted)' }}>{fmt(r.screenings)}</span> },
    { key: 'occupancy', header: 'תפוסה', align: 'center', render: (r) => <Occ v={r.occupancy} plain={!withChange} /> },
    { key: 'weeks', header: 'שבועות', align: 'center', render: (r) => <span style={{ color: 'var(--muted)' }}>{r.weeks || '—'}</span> },
    { key: 'total', header: 'סה״כ', align: 'center', render: (r) => <span style={{ color: 'var(--txt)' }}>{fmt(r.total)}</span> },
  ];

  return (
    <div style={{ maxWidth: PAGE_MAX, margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <ChipRow>
        {MODES.map((m) => <Chip key={m.key} label={m.label} active={sel.mode === m.key} onClick={() => pick(m.key)} />)}
      </ChipRow>
      {sel.mode === 'custom' && (
        <CustomRange value={{ start: sel.start, end: sel.end }} onChange={(r) => setSel({ ...sel, ...r })} />
      )}
      <div data-ranking style={{ ...card, padding: mob ? 16 : 22, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }}>
        <Head mob={mob} title={TITLE[sel.mode]} total={data?.total || 0}
          nav={<Stepper label={periodLabel(sel, today)} onPrev={steppable ? () => step(-1) : null}
                        onNext={steppable ? () => step(1) : null} />}
          tag={sel.start > today && <PresaleTag />}
          line={data && <>
            {withChange && data.prev > 0 && <Change curr={data.total} prev={data.prev} />}
            {data.screenings > 0 && <span>{fmt(data.screenings)} הקרנות · תפוסה {data.occupancy}%</span>}
          </>} />
        {!data || rows.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--faint)', padding: 30, fontSize: 13 }}>
            {loading ? 'טוען נתונים...' : 'אין נתונים לתקופה זו'}
          </div>
        ) : (
          <>
            <Podium items={rows} mob={mob} max={5} itemKey={(r) => r.movie_id}
              onClick={(r) => onMovieClick && onMovieClick(r.movie_id)}
              poster={(r) => filmPoster(r, 'xl')} name={filmName} value={(r) => r.tickets}
              extra={(r) => (!r.prev || r.wednesday) && (
                <>{!r.prev && <NewBadge />}{r.wednesday && <WednesdayChip tickets={r.previewTickets} />}</>
              )} />
            {mob ? (
              <div data-phone-list>
                {rows.slice(0, shown).map((r, i) => (
                  <PhoneRow key={r.movie_id} i={i} poster={filmPoster(r, 'sm')} name={filmName(r)}
                    sub={r.wednesday ? <WednesdayChip tickets={r.previewTickets} />
                      : (r.title_en && r.title_en !== filmName(r) ? r.title_en : null)}
                    tickets={r.tickets}
                    line1={<>{withChange && <><Change curr={r.tickets} prev={r.prev} size={10} /> · </>}<Occ v={r.occupancy} plain={!withChange} /></>}
                    line2={`${fmt(r.screenings)} הקרנות${r.total ? ` · סה״כ ${fmt(r.total)}` : ''}`}
                    onClick={() => onMovieClick && onMovieClick(r.movie_id)} />
                ))}
                {rows.length > shown && <MoreButton onClick={() => setShown((n) => n + 20)}>הצג עוד</MoreButton>}
              </div>
            ) : (
              <RankingTable columns={cols} data={rows} rankColumn pageSize={20} minWidth={760}
                            initialSort={{ key: 'tickets', dir: 'desc' }}
                            onRowClick={(r) => onMovieClick && onMovieClick(r.movie_id)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ChartsPage;
